import { useMemo, useState } from 'react'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useSelectedItemsList } from 'contexts/SelectedItemsListProvider'
import { useNicheMutations } from 'hooks-queries/niche'
import { useTranslate } from 'lib/tolgee'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import { RENAME_NICHES_LOG } from './rename-niches-form.consts'
import type { IRenameNichesForm, IRenameNichesFormBase } from './rename-niches-form.types'

export const useRenameNichesFormModel = ({ handleClose }: IRenameNichesForm): IRenameNichesFormBase => {
  const [nichesToRename, setNichesToRename] = useState<Record<string, string>>({})
  const [isConfirmView, setIsConfirmView] = useState(false)

  const { selectedItemsBar } = useSelectedItemsList()
  const { createToast } = useToast()
  const t = useTranslate()

  const [renameNiches, { loading }] = useNicheMutations({
    mutationName: 'renameNiches',
    errorLog: RENAME_NICHES_LOG,
  })

  const handleChange: React.ChangeEventHandler<HTMLInputElement> &
    React.ChangeEventHandler<HTMLTextAreaElement> = e => {
    const name = e.target.name
    const value = e.target.value
    setNichesToRename(prevState => {
      return {
        ...prevState,
        [name]: value,
      }
    })
  }

  const handleViewConfirmation = () => {
    setIsConfirmView(true)
  }

  const handleSubmit = () => {
    const nichesIds = Object.keys(nichesToRename)

    const eventData = nichesIds.reduce(
      (prev, curr) => {
        const nicheId = `niche.${curr}`
        const newName = nichesToRename[curr]
        const nicheData = selectedItemsBar.find(item => item.id === curr)
        const oldName = nicheData.name
        return {
          nicheIds: [...prev.nicheIds, nicheId],
          'Old Niche Names': [...prev['Old Niche Names'], oldName],
          'New Niche Names': [...prev['New Niche Names'], newName],
        }
      },
      {
        nicheIds: [],
        'Old Niche Names': [],
        'New Niche Names': [],
      }
    )

    const { NICHE_EDITING } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(NICHE_EDITING.RENAME_NICHES, eventData)

    const nichesList = nichesIds.map(niche => {
      const newName = nichesToRename[niche]
      return {
        newName: newName,
        territoryId: `niche.${niche}`,
      }
    })
    renameNiches({
      variables: {
        input: {
          niches: nichesList,
        },
      },
    })
      .then(data => {
        if (data.data?.updateNicheName?.success) {
          handleClose()
          window.location.reload()
          return
        }
        createToast({
          content: t(TOLGEE_GLOBALS.somethingWentWrong),
        })
      })
      .catch(() => {
        createToast({
          content: t(TOLGEE_GLOBALS.somethingWentWrong),
        })
      })
      .finally(() => {
        handleClose()
      })
  }

  const isSubmitButtonDisabled = useMemo(() => {
    const keys = Object.keys(nichesToRename)
    return keys.some(key => !nichesToRename[key])
  }, [nichesToRename])

  const handleCancel = () => {
    const { NICHE_EDITING } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(NICHE_EDITING.CLOSE_MODAL)

    handleClose()
  }

  return {
    nicheList: selectedItemsBar,
    handleChange,
    nichesToRename,
    handleSubmit,
    isSubmitButtonDisabled,
    isMutationLoading: loading,
    handleViewConfirmation,
    isConfirmView,
    handleCancel,
  }
}
